import { Minus, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import { Slider } from "../ui/slider";
import { MAX_ROOM_PARTICIPANTS } from "../../../shared/room-limits.ts";

// A stepped participant limit: the first step is "no limit" (∞), then 1 up to
// the current server maximum. `null` means no limit, available only at the hard ceiling.
export function ParticipantLimitSlider({
  value,
  onChange,
  onBlur,
  labelId,
  describedBy,
  disabled,
  max = MAX_ROOM_PARTICIPANTS,
  invalid,
}: {
  value: number | null;
  onChange: (_value: number | null) => void;
  onBlur?: () => void;
  labelId: string;
  describedBy?: string;
  disabled?: boolean;
  max?: number;
  invalid?: boolean;
}) {
  const { t } = useTranslation();
  const min = max < MAX_ROOM_PARTICIPANTS ? 1 : 0;
  const step = Math.max(min, Math.min(max, value ?? 0));
  const aboveMax = value === null ? min > 0 : value > max;
  const set = (next: number) =>
    onChange(next <= 0 && min === 0 ? null : Math.max(min, Math.min(max, Math.round(next))));
  return (
    <div className="flex items-center gap-3">
      <Button
        type="button"
        variant="outline"
        size="icon-sm"
        aria-label={t("home.limitDecrease")}
        disabled={disabled || (!aboveMax && step <= min)}
        onClick={() => set(aboveMax ? max : step - 1)}
      >
        <Minus />
      </Button>
      <Slider
        min={min}
        max={max}
        step={1}
        value={[step]}
        onValueChange={([next]) => set(next)}
        onBlur={onBlur}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        aria-labelledby={labelId}
        aria-describedby={describedBy}
        aria-valuetext={step ? String(step) : t("home.unlimited")}
      />
      <Button
        type="button"
        variant="outline"
        size="icon-sm"
        aria-label={t("home.limitIncrease")}
        disabled={disabled || step >= max}
        onClick={() => set(step + 1)}
      >
        <Plus />
      </Button>
      {/* The slider's aria-valuetext already announces the value. */}
      <span aria-hidden="true" className="min-w-6 text-right text-sm font-medium tabular-nums">
        {step || "∞"}
      </span>
    </div>
  );
}
